import { Button, Card, Descriptions, Form, Modal, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import {
    loanGetAction,
    loanInternalCommentsAction,
} from '~Loans/actions';
import { loanSelector } from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import InterestMethodTag from '~Loans/Loan/InterestMethodTag';
import { EditOutlined } from '@ant-design/icons';
import ModalStateEnum from '~UI/ModalStateEnum';
import WysiwygEditor from '~UI/WysiwygEditor';

interface IMatch {
    uuid: string;
}

interface IState {
    internalCommentsModalState: ModalStateEnum;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    loan: ILoan;
}

interface IPropsDispatch {
    loanGet: () => void;
    loanInternalComments: (internalComments: string) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Overview extends React.Component<Props, IState> {
    public state: IState = {
        internalCommentsModalState: ModalStateEnum.Off,
    };

    constructor(props: Props) {
        super(props);

        this.onChangeInternalComments = this.onChangeInternalComments.bind(this);
        this.onClickEditInternalComments = this.onClickEditInternalComments.bind(this);
        this.onCloseInternalCommentsModal = this.onCloseInternalCommentsModal.bind(this);
    }

    public componentDidMount(): void {
        const { loan } = this.props;

        if (!loan) {
            this.props.loanGet();
        }
    }

    public render(): JSX.Element {
        const { loan, match } = this.props;
        const { internalCommentsModalState } = this.state;

        if (!loan) {
            return (
                <Layout uuid={match.params.uuid} section='overview'>
                    <Typography.Title level={2}>Overview</Typography.Title>
                    <Spin />
                </Layout>
            );
        }

        const currencyFormatter = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        const internalCommentsEditModalBlock: JSX.Element = ModalStateEnum.Off !== internalCommentsModalState && (
            <Modal
                cancelText='Close'
                className='loan-internal-comments-modal'
                okButtonProps={{ style: { display: 'none' } }}
                onCancel={this.onCloseInternalCommentsModal}
                open={internalCommentsModalState === ModalStateEnum.Open}
                title='Internal Comments'
                width={1000}
            >
                <Form.Item className='internal-comments'>
                    <WysiwygEditor
                        bold={true}
                        italic={true}
                        link={true}
                        onChange={this.onChangeInternalComments}
                        orderedList={true}
                        underline={true}
                        unorderedList={true}
                        value={loan.internalComments}
                    />
                </Form.Item>
            </Modal>
        );

        return (
            <Layout uuid={match.params.uuid} section='overview'>
                <Typography.Title level={2}>Overview</Typography.Title>
                <Typography.Title className='internal-comments' level={3}>
                    <Space>
                        <>Internal Comments</>
                        <Button className='edit' type='link' onClick={this.onClickEditInternalComments}>
                            <EditOutlined />
                        </Button>
                    </Space>
                </Typography.Title>
                <Card>
                    <div dangerouslySetInnerHTML={{ __html: loan.internalComments || '-' }} />
                </Card>
                {internalCommentsEditModalBlock}
                <Typography.Title level={3}>Details</Typography.Title>
                <Descriptions bordered={true} layout='vertical'>
                    <Descriptions.Item label='Start Date'>{dayjs(loan.startDate).format('Do MMMM YYYY')}</Descriptions.Item>
                    <Descriptions.Item label='End Date'>{dayjs(loan.endDate).format('Do MMMM YYYY')}</Descriptions.Item>
                    <Descriptions.Item label='Next Payment Date'>{loan.paymentDateNext ? dayjs(loan.paymentDateNext).format('Do MMMM YYYY') : '-'}</Descriptions.Item>
                    <Descriptions.Item label='Next Payment Amount'>
                        <Space>
                            {loan.paymentAmountNext ? currencyFormatter.format(loan.paymentAmountNext) : '-'}
                            <InterestMethodTag loan={loan} />
                        </Space>
                    </Descriptions.Item>
                </Descriptions>
            </Layout>
        );
    }

    private onClickEditInternalComments(): void {
        this.setState({
            internalCommentsModalState: ModalStateEnum.Open,
        });
    }

    private onCloseInternalCommentsModal(): void {
        this.setState({ internalCommentsModalState: ModalStateEnum.Closed });
    }

    private onChangeInternalComments(internalComments: string): void {
        this.props.loanInternalComments(internalComments);
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        loan: loanSelector(state, ownProps.match.params.uuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        loanGet: () => dispatch(loanGetAction(ownProps.match.params.uuid)),
        loanInternalComments: (internalComments: string) => dispatch(loanInternalCommentsAction(ownProps.match.params.uuid, internalComments)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Overview);
